import React from 'react';
import { Box, Typography, Fade, Paper, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Calendar, Check } from 'lucide-mui';
import { useNavigate } from 'react-router-dom';
import useUserTags from '../../../../hooks/useTags';
import type { GlobalDialogProps } from '../../../../types';

// Reuse the same animation duration as in the parent component
const ANIMATION_DURATION = 400;

interface CalendarConnectionScreenProps {
  onClose?: GlobalDialogProps['onClose'];
}

const CalendarConnectionScreen: React.FC<CalendarConnectionScreenProps> = ({
  onClose,
}) => {
  const navigate = useNavigate();
  const { setTag } = useUserTags();
  const theme = useTheme();

  const handleConnectCalendar = () => {
    setTag({
      key: 'calendarOnboardingSource',
      value: 'onboarding-connect',
    });

    // Close the onboarding modal if onClose is provided
    if (onClose) {
      onClose();
    }

    navigate('/settings/meetingbot');
  };

  return (
    <Fade in timeout={ANIMATION_DURATION}>
      <Box
        sx={{
          width: '100%',
          textAlign: 'center',
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 300, mb: 2 }}>
          Connect Your Calendar
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ mb: 4, textAlign: 'center', maxWidth: '700px', mx: 'auto' }}
        >
          Our meeting bot automatically joins and records your meetings
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: '500px',
            width: '100%',
            bgcolor:
              theme.palette.mode === 'dark'
                ? 'background.paper'
                : theme.palette.grey[50],
            borderRadius: 4,
            textAlign: 'center',
          }}
        >
          <Calendar
            sx={{
              fontSize: 50,
              mb: 3,
              color: 'primary.main',
              strokeWidth: 1.5,
            }}
          />

          <Typography variant="h6" sx={{ mb: 3 }}>
            Meeting Bot Integration
          </Typography>

          <Box
            sx={{
              mb: 4,
              textAlign: 'left',
            }}
          >
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Connect your calendar to automatically record:
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    bgcolor: 'success.main',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Check sx={{ fontSize: 14, color: 'white' }} />
                </Box>
                <Typography variant="body2">Google Meet calls</Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    bgcolor: 'success.main',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Check sx={{ fontSize: 14, color: 'white' }} />
                </Box>
                <Typography variant="body2">
                  Microsoft Teams meetings
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    bgcolor: 'success.main',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Check sx={{ fontSize: 14, color: 'white' }} />
                </Box>
                <Typography variant="body2">
                  Generate AI summaries and transcripts
                </Typography>
              </Box>
            </Box>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={handleConnectCalendar}
            sx={{
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
            }}
          >
            Set Up Meeting Bot
          </Button>
        </Paper>
      </Box>
    </Fade>
  );
};

export default CalendarConnectionScreen;
